import React, {
  FC,
  MouseEvent,
  PointerEvent,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import * as Styles from './filterRail.module.scss'

/**
 * Default padding that separates the outer pills from the edges of the filter rail when `outerPadding` is not
 * specified.
 */
const DEFAULT_OUTER_PADDING = '1rem'

/**
 * A single filter option in the filter rail.
 */
type FilterOption = {
  /**
   * The text to display inside the pill associated with this filter option.
   */
  label: string
  /**
   * The underlying, unique value associated with this filter option.
   */
  value: string
}

/**
 * Props for the `FilterRail` component.
 */
export type FilterRailProps = {
  /**
   * Optional additional class name(s) to apply to the root element of the filter rail.
   */
  className?: string
  /**
   * The list of filter options to display in the filter rail. Each option must have a unique `value`.
   */
  items?: FilterOption[]
  /**
   * In controlled mode, the currently selected filter value. If not provided, the component will manage its own state.
   */
  value?: string | undefined
  /**
   * The padding to apply to the left and right edges of the filter rail. This padding separates the outer pills from
   * the edges of the rail. If not provided, a default padding of `1rem` will be used.
   */
  outerPadding?: string
  /**
   * The label to display for the "empty filter" option.
   */
  emptyFilterLabel: string
  /**
   * Callback fired when the selected filter value changes. Receives value of the selected filter or `undefined` if
   * the filter was cleared.
   */
  onChange?: (value: string | undefined) => void
}

/**
 * A horizontall, draggable filter rail that displays a list of filter options as pills.
 *
 * The first pill always represents the "empty filter" option that is marked as active when no other filter is
 * selected, and empties the filter when clicked.
 *
 * Users can
 */
export const FilterRail: FC<FilterRailProps> = ({
  className = '',
  items = [],
  value = undefined,
  onChange,
  emptyFilterLabel,
  outerPadding = DEFAULT_OUTER_PADDING,
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const emptyFilterButtonRef = useRef<HTMLButtonElement | null>(null)
  const thresholdRef = useRef(10)

  const calibrate = useCallback(() => {
    const track = trackRef.current
    if (!track) {
      return
    }

    const trackComputedStyle = getComputedStyle(track)
    const paddingLeftPx = parseFloat(trackComputedStyle.paddingLeft) || 0
    const gapPx = parseFloat(trackComputedStyle.columnGap) || 0
    thresholdRef.current = (paddingLeftPx + gapPx) / 2
  }, [])

  useLayoutEffect(() => {
    calibrate()
  }, [calibrate, items])

  const DRAG_SLOP = 4
  const panRef = useRef<{
    x: number
    left: number
    id: number
    last?: { x: number; t: number }
    v?: number
  } | null>(null)
  const draggedRef = useRef(false)
  const [panning, setPanning] = useState(false)

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) {
      return
    }

    const track = trackRef.current
    if (!track) {
      return
    }

    panRef.current = {
      x: event.clientX,
      left: track.scrollLeft,
      id: event.pointerId,
    }
    draggedRef.current = false
  }

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const pan = panRef.current
    const el = trackRef.current
    if (!pan || !el || event.pointerId !== pan.id) {
      return
    }

    const dx = event.clientX - pan.x

    if (!draggedRef.current) {
      if (Math.abs(dx) < DRAG_SLOP) {
        return
      }
      draggedRef.current = true
      setPanning(true)
      el.style.scrollSnapType = 'none'
      try {
        el.setPointerCapture(pan.id)
      } catch {
        // Intentionally empty block
      }
    }

    const now = performance.now()
    const prev = pan.last
    pan.last = { x: event.clientX, t: now }
    if (prev && now > prev.t) {
      pan.v = (event.clientX - prev.x) / (now - prev.t)
    }

    el.scrollLeft = pan.left - dx
  }

  const closestEdge = (track: HTMLDivElement, target: number) => {
    const padLeft = parseFloat(getComputedStyle(track).paddingLeft) || 0
    const trackLeft = track.getBoundingClientRect().left
    let best = target
    let distance = Infinity
    Array.from(track.children).forEach(pill => {
      const edge =
        track.scrollLeft +
        (pill.getBoundingClientRect().left - trackLeft) -
        padLeft
      const d = Math.abs(edge - target)
      if (d < distance) {
        distance = d
        best = edge
      }
    })
    return best
  }

  const endPan = () => {
    const track = trackRef.current

    const pan = panRef.current
    panRef.current = null

    if (!pan || !track || !draggedRef.current) {
      return
    }

    setPanning(false)

    try {
      track.releasePointerCapture(pan.id)
    } catch {
      // Intentionally empty block
    }

    const reduceMotion =
      typeof matchMedia !== 'undefined' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches

    const projection = 180
    const max = Math.max(track.scrollWidth - track.clientWidth, 0)
    const projected = track.scrollLeft - (pan.v || 0) * projection
    const target = Math.min(Math.max(closestEdge(track, projected), 0), max)

    const restoreSnap = () => {
      track.style.scrollSnapType = 'x proximity'
    }

    if (reduceMotion || Math.abs(target - track.scrollLeft) < 1) {
      track.scrollLeft = target
      restoreSnap()
      return
    }

    let done = false
    const end = () => {
      if (done) return
      done = true
      track.removeEventListener('scrollend', end)
      restoreSnap()
    }
    track.addEventListener('scrollend', end)
    setTimeout(end, 700)

    track.scrollTo({ left: target, behavior: 'smooth' })
  }

  const onClickCapture = (event: MouseEvent<HTMLElement>) => {
    if (!draggedRef.current) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    draggedRef.current = false
  }

  const snapToStart = (domIndex: number) => {
    const track = trackRef.current
    if (!track) {
      return
    }

    const pill = track.children[domIndex]
    if (!pill) {
      return
    }

    const padLeft = parseFloat(getComputedStyle(track).paddingLeft) || 0
    const delta =
      pill.getBoundingClientRect().left - track.getBoundingClientRect().left
    const max = Math.max(track.scrollWidth - track.clientWidth, 0)
    const target = Math.min(
      Math.max(track.scrollLeft + delta - padLeft, 0),
      max
    )

    const reduceMotion =
      typeof matchMedia !== 'undefined' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches

    track.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' })
  }

  const handlePress = (item: FilterOption, i: number) => {
    if (!onChange) {
      return
    }

    const deactivatingFilter = item.value === value
    onChange(deactivatingFilter ? undefined : item.value)

    const emptyFilterIndex = 0
    const newFilterIndex = i + 1
    snapToStart(deactivatingFilter ? emptyFilterIndex : newFilterIndex)

    if (deactivatingFilter && emptyFilterButtonRef.current) {
      // Prevent built-in scroll when focusing newly active pill
      emptyFilterButtonRef.current.focus({ preventScroll: true })
    }
  }

  return (
    <div className={`${Styles.filterRail} ${className}`}>
      <div
        ref={trackRef}
        className={`${Styles.track} ${panning ? Styles.panning : ''}`}
        style={{
          paddingLeft: outerPadding,
          paddingRight: outerPadding,
          scrollPaddingLeft: outerPadding,
          scrollPaddingRight: outerPadding,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPan}
        onPointerCancel={endPan}
        onClickCapture={onClickCapture}
        onDragStart={e => e.preventDefault()}
      >
        <button
          ref={emptyFilterButtonRef}
          type="button"
          onClick={() => {
            if (onChange) onChange(undefined)
            snapToStart(0)
          }}
          aria-pressed={value === undefined}
          style={{ scrollSnapAlign: 'start' }}
          className={`${Styles.pill} ${
            value === undefined ? Styles.active : Styles.inactive
          }`}
        >
          {emptyFilterLabel}
        </button>

        {items.map((item, i) => (
          <button
            key={item.value}
            type="button"
            onClick={() => handlePress(item, i)}
            aria-pressed={item.value === value}
            style={{ scrollSnapAlign: 'start' }}
            className={`${Styles.pill} ${
              item.value === value ? Styles.active : Styles.inactive
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  )
}
